
import React from 'react';
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { UserIcon, Sparkles, Heart } from "lucide-react";
import { motion } from "framer-motion";
import { ButterflyAndLotus } from '../visual/ButterflyAndLotus';
import { useTranslations } from '../../i18n/hooks';

export const ParentSection: React.FC = () => {
  const t = useTranslations('index');

  return (
    <motion.div 
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
      className="relative overflow-hidden p-6 flex items-center justify-center role-card"
    >
      {/* Background gradient - dark mode */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#1A1F2E] to-[#0A0F1E] dark:block light:hidden" />
      
      {/* Background gradient - light mode */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#E5F0FF] to-[#F8FBFF] dark:hidden light:block" />
      
      {/* Content */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3, duration: 0.8 }}
        className="relative max-w-md mx-auto space-y-8 text-center z-10"
      >
        <div className="relative backdrop-blur-xl dark:bg-slate-900/70 light:bg-white/70 p-8 rounded-3xl dark:border dark:border-blue-500/50 light:border-blue-200 shadow-2xl
                      dark:hover:border-blue-400/60 light:hover:border-blue-400 transition-all duration-300
                      dark:shadow-[0_0_25px_rgba(59,130,246,0.2)] light:shadow-[0_0_25px_rgba(59,130,246,0.1)]">
          <div className="relative mb-8 transform transition-all duration-300 hover:scale-105 role-image-container">
            <ButterflyAndLotus />
          </div>
          <div className="space-y-5 mb-8">
            <motion.h2 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="text-3xl font-bold dark:text-white light:text-slate-800 drop-shadow-sm role-card-title"
            >
              {t('parent.title')}
            </motion.h2>
            <motion.p 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7 }}
              className="dark:text-gray-100 light:text-slate-600 leading-relaxed drop-shadow-sm role-card-description"
            >
              {t('parent.description')}
            </motion.p>
          </div>

          <div className="space-y-4">
            <Button
              asChild
              variant="high-contrast"
              className="w-full dark:bg-gradient-to-r dark:from-blue-600 dark:to-cyan-500 light:bg-blue-500 dark:text-white light:text-white font-medium text-lg py-6
                       dark:hover:from-blue-700 dark:hover:to-cyan-600 light:hover:bg-blue-600
                       transition-all duration-300 shadow-lg hover:shadow-blue-500/25 hover:scale-[1.02]
                       active:scale-[0.98] group relative overflow-hidden parent-sign-in"
            >
              <Link to="/signin" className="inline-flex items-center justify-center gap-2">
                <div className="absolute inset-0 dark:bg-white/20 light:bg-blue-200/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300" />
                <UserIcon className="w-5 h-5 relative z-10" />
                <span className="relative z-10 drop-shadow-sm">{t('parent.signIn')}</span>
              </Link>
            </Button>
            <Button
              asChild
              variant="high-contrast-outline"
              className="w-full backdrop-blur-sm dark:border-2 dark:border-white light:border-2 light:border-blue-200 text-white font-medium text-lg py-6
                         dark:hover:bg-white/10 light:hover:bg-blue-200/10 transition-all duration-300
                         shadow-lg hover:shadow-blue-500/25 hover:scale-[1.02] active:scale-[0.98]
                         relative overflow-hidden group"
            >
              <Link to="/whatsapp-community" className="inline-flex items-center justify-center gap-2">
                <div className="absolute inset-0 dark:bg-gradient-to-r dark:from-blue-500/20 dark:via-cyan-500/20 dark:to-teal-500/20 light:bg-blue-200/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300" />
                <Heart className="w-5 h-5 relative z-10" />
                <span className="relative z-10 drop-shadow-sm">{t('parent.register')}</span>
              </Link>
            </Button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};
